/**
 * SwipeActionsHook - Horizontal swipe to reveal action buttons
 *
 * Features:
 * - Swipe left to reveal action buttons on the right
 * - Velocity-based detection for quick swipes
 * - Spring-back animation on incomplete swipes
 * - Tap outside to close revealed actions
 * - Prevents vertical scroll during horizontal swipe
 *
 * Usage:
 * <div phx-hook="SwipeActionsHook"
 *      data-action-width="160"
 *      id="row-123">
 *   <div data-swipe-content>
 *     <!-- Main row content -->
 *   </div>
 *   <div data-swipe-actions>
 *     <!-- Action buttons revealed on swipe -->
 *   </div>
 * </div>
 */
const SwipeActionsHook = {
  mounted() {
    // Configuration
    this.actionWidth = parseInt(this.el.dataset.actionWidth || '160', 10);
    this.swipeThreshold = 0.4; // 40% of action width to trigger open
    this.velocityThreshold = 0.5; // pixels per ms for quick swipe

    // State
    this.isOpen = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.startTime = 0;
    this.isSwiping = false;
    this.isScrolling = null; // null = undetermined, true = vertical, false = horizontal

    // Elements
    this.content = this.el.querySelector('[data-swipe-content]');
    this.actions = this.el.querySelector('[data-swipe-actions]');

    if (!this.content || !this.actions) {
      console.warn('SwipeActionsHook: Missing data-swipe-content or data-swipe-actions elements');
      return;
    }

    // Set up initial styles
    this.setupStyles();

    // Bind event handlers
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleOutsideTouch = this.handleOutsideTouch.bind(this);

    // Attach listeners
    this.el.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.el.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.el.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Listen for outside touches to close
    document.addEventListener('touchstart', this.handleOutsideTouch, { passive: true });

    // Store reference for cleanup
    this.el._swipeHook = this;
  },

  destroyed() {
    this.el.removeEventListener('touchstart', this.handleTouchStart);
    this.el.removeEventListener('touchmove', this.handleTouchMove);
    this.el.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('touchstart', this.handleOutsideTouch);
    // Clear reference to prevent memory leak
    this.el._swipeHook = null;
  },

  setupStyles() {
    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.transitionDuration = prefersReducedMotion ? '0ms' : '200ms';

    // Container should hide overflow
    this.el.style.overflow = 'hidden';
    this.el.style.position = 'relative';

    // Content should be transformable
    this.content.style.transition = `transform ${this.transitionDuration} ease-out`;
    this.content.style.willChange = 'transform';

    // Actions positioned behind content — hidden until swipe begins
    this.actions.style.position = 'absolute';
    this.actions.style.right = '0';
    this.actions.style.top = '0';
    this.actions.style.bottom = '0';
    this.actions.style.width = `${this.actionWidth}px`;
    this.actions.style.display = 'flex';
    this.actions.style.alignItems = 'stretch';
    this.actions.style.justifyContent = 'flex-end';
    this.actions.style.visibility = 'hidden';
  },

  handleTouchStart(e) {
    // Close any other open swipe actions first
    document.querySelectorAll('[phx-hook="SwipeActionsHook"]').forEach(el => {
      if (el !== this.el && el._swipeHook && el._swipeHook.isOpen) {
        el._swipeHook.close();
      }
    });

    // Ensure actions are hidden at the start of every new touch —
    // guards against the 220ms close() timeout race condition
    if (!this.isOpen) {
      this.actions.style.visibility = 'hidden';
    }

    const touch = e.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.startTime = Date.now();
    this.isScrolling = null;
    this.isSwiping = true;

    // Remove transition during drag for responsiveness
    this.content.style.transition = 'none';
  },

  handleTouchMove(e) {
    if (!this.isSwiping) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - this.startX;
    const deltaY = touch.clientY - this.startY;

    // Determine scroll direction on first significant movement
    if (this.isScrolling === null) {
      if (Math.abs(deltaY) > 10 || Math.abs(deltaX) > 10) {
        this.isScrolling = Math.abs(deltaY) > Math.abs(deltaX);
      }
    }

    // If vertical scroll, don't interfere
    if (this.isScrolling) {
      this.isSwiping = false;
      this.content.style.transition = `transform ${this.transitionDuration} ease-out`;
      return;
    }

    // Prevent page scroll during horizontal swipe
    e.preventDefault();

    // Show action buttons once a horizontal swipe is confirmed
    this.actions.style.visibility = 'visible';

    // Calculate new position
    let newX;
    if (this.isOpen) {
      // Already open, allow drag from open position
      newX = -this.actionWidth + deltaX;
    } else {
      // Closed, only allow left swipe
      newX = Math.min(0, deltaX);
    }

    // Apply resistance at boundaries
    if (newX > 0) {
      newX = newX * 0.3; // Resistance when pulling right past closed
    } else if (newX < -this.actionWidth) {
      const overflow = newX + this.actionWidth;
      newX = -this.actionWidth + (overflow * 0.3); // Resistance past open
    }

    this.currentX = newX;
    this.content.style.transform = `translateX(${newX}px)`;
  },

  handleTouchEnd(e) {
    if (!this.isSwiping || this.isScrolling) {
      this.isSwiping = false;
      return;
    }

    this.isSwiping = false;
    this.content.style.transition = 'transform 200ms ease-out';

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - this.startX;
    const deltaTime = Date.now() - this.startTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Determine final state based on position and velocity
    // deltaX > 0 = right swipe (to close), deltaX < 0 = left swipe (to open)
    const threshold = this.actionWidth * this.swipeThreshold;

    let shouldBeOpen;
    if (this.isOpen) {
      // When open: right swipe past threshold OR quick right swipe should close
      const shouldClose = deltaX > threshold || (deltaX > 0 && velocity > this.velocityThreshold);
      shouldBeOpen = !shouldClose;
    } else {
      // When closed: left swipe past threshold OR quick left swipe should open
      shouldBeOpen = deltaX < -threshold || (deltaX < 0 && velocity > this.velocityThreshold);
    }

    if (shouldBeOpen && !this.isOpen) {
      this.open();
    } else if (!shouldBeOpen && this.isOpen) {
      this.close();
    } else {
      // Snap back to current state
      this.snapToState();
    }
  },

  handleOutsideTouch(e) {
    if (this.isOpen && !this.el.contains(e.target)) {
      this.close();
    }
  },

  open() {
    this.isOpen = true;
    this.content.style.transform = `translateX(-${this.actionWidth}px)`;
    this.el.classList.add('swipe-open');

    // Announce to screen readers
    this.el.setAttribute('aria-expanded', 'true');
  },

  close() {
    this.isOpen = false;
    this.content.style.transform = 'translateX(0)';
    this.el.classList.remove('swipe-open');

    // Hide action buttons after the slide-back animation completes
    setTimeout(() => {
      if (!this.isOpen) {
        this.actions.style.visibility = 'hidden';
      }
    }, 220);

    // Announce to screen readers
    this.el.setAttribute('aria-expanded', 'false');
  },

  snapToState() {
    if (this.isOpen) {
      this.content.style.transform = `translateX(-${this.actionWidth}px)`;
    } else {
      this.content.style.transform = 'translateX(0)';
    }
  }
};

export default SwipeActionsHook;
